import React, { useState } from "react";
import { ListChecks, ScanFace, ChevronRight, ChevronLeft, CheckCircle2, Circle, Loader2 } from "lucide-react";

// Same collapsible blue-icon-dock pattern as the Hirality Meetings live-call
// sidebar (see HiralityMeetings/LiveTranscriptPanel.jsx): a small round
// entry point that expands into themed tool icons, each opening its own
// drawer. Two tools here, matching what this guided flow actually has —
// step-by-step progress, and the verification results as they land.
const TOOLS = [
  { key: "progress", icon: ListChecks, label: "Verification Progress" },
  { key: "results",  icon: ScanFace,   label: "Verification Results" },
];

export default function EkycDock({ steps, currentStep, result, talking }) {
  const [open, setOpen] = useState(true);
  const [activeTool, setActiveTool] = useState("progress");

  const hasResults = !!(result?.faceMatch || result?.liveness || result?.ocr);

  return (
    <>
      <style>{`
        @keyframes ekycSlideIn { from { transform: translateX(24px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes ekycFadeUp  { from { transform: translateY(6px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes ekycPulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
        .ekyc-line { animation: ekycFadeUp 0.25s ease both; }
        .ekyc-spin { animation: ekycSpin 0.8s linear infinite; }
        @keyframes ekycSpin { to { transform: rotate(360deg); } }
        .ekyc-dock-btn:hover { background: rgba(255,255,255,0.09) !important; }
      `}</style>

      {!open && (
        <button onClick={() => setOpen(true)} title="Show verification tools"
          style={{ position: "fixed", top: "50%", right: 0, transform: "translateY(-50%)", zIndex: 1000,
            background: "linear-gradient(135deg,#00C8FF,#7C5CFF)", border: "none", borderRadius: "10px 0 0 10px",
            padding: "14px 8px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
            boxShadow: "-4px 0 20px rgba(0,0,0,0.3)" }}>
          <ListChecks size={16} color="#fff" />
          <ChevronLeft size={14} color="#fff" />
        </button>
      )}

      {open && (
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 999, display: "flex", fontFamily: "Outfit,sans-serif" }}>
          {activeTool && (
            <div style={{ width: 320, background: "rgba(10,20,35,0.92)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
              borderLeft: "1px solid rgba(0,200,255,0.15)", display: "flex", flexDirection: "column", animation: "ekycSlideIn 0.25s ease both" }}>

              {activeTool === "progress" && (
                <>
                  <div style={{ padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 8 }}>
                    <ListChecks size={14} color="#00C8FF" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#E8F0FE" }}>Verification Progress</span>
                  </div>
                  <div style={{ flex: 1, overflowY: "auto", padding: "14px 18px", display: "flex", flexDirection: "column", gap: 4 }}>
                    {steps.map((s, i) => {
                      const isDone = i < currentStep;
                      const isActive = i === currentStep;
                      return (
                        <div key={s.id} className="ekyc-line" style={{ display: "flex", gap: 10, padding: "9px 0" }}>
                          {isDone ? <CheckCircle2 size={16} color="#00E5A3" style={{ flexShrink: 0, marginTop: 1 }} />
                            : isActive ? <Loader2 size={16} color="#00C8FF" className="ekyc-spin" style={{ flexShrink: 0, marginTop: 1 }} />
                            : <Circle size={16} color="#3D5473" style={{ flexShrink: 0, marginTop: 1 }} />}
                          <div>
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: isActive ? "#E8F0FE" : isDone ? "#C8D6E8" : "#5A7291" }}>{s.name}</div>
                            {isActive && (
                              <div style={{ fontSize: 11, color: "#7A93B4", marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
                                {talking && <Loader2 size={9} className="ekyc-spin" />} {s.initialMessage}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ padding: "10px 18px", borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: 10.5, color: "#3D5473", textAlign: "center" }}>
                    Step {Math.min(currentStep + 1, steps.length)} of {steps.length}
                  </div>
                </>
              )}

              {activeTool === "results" && (
                <>
                  <div style={{ padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 8 }}>
                    <ScanFace size={14} color="#00C8FF" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#E8F0FE" }}>Verification Results</span>
                  </div>
                  <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px" }}>
                    {!hasResults ? (
                      <div style={{ textAlign: "center", color: "#3D5473", fontSize: 12.5, marginTop: 40 }}>
                        Results appear here as each check completes.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {result.faceMatch && (
                          <div className="ekyc-line" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 12px" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#7A93B4", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>Face Match</div>
                            <div style={{ fontSize: 13, color: result.faceMatch.verified ? "#00E5A3" : "#FF4D6D", fontWeight: 700 }}>
                              {result.faceMatch.verified ? "Matched" : "No Match"}
                            </div>
                            {result.faceMatch.score != null && <div style={{ fontSize: 11.5, color: "#C8D6E8", marginTop: 2 }}>Confidence: {result.faceMatch.score.toFixed?.(2) ?? result.faceMatch.score}%</div>}
                          </div>
                        )}
                        {result.liveness && (
                          <div className="ekyc-line" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 12px" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#7A93B4", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>Liveness</div>
                            <div style={{ fontSize: 13, color: result.liveness.liveness ? "#00E5A3" : "#FF4D6D", fontWeight: 700 }}>
                              {result.liveness.liveness ? "Passed" : "Failed"}
                            </div>
                            <div style={{ fontSize: 11.5, color: "#C8D6E8", marginTop: 2 }}>
                              Head turn: {result.liveness.head_turn || "—"} · Blink: {result.liveness.blink ? "Yes" : "No"}
                            </div>
                          </div>
                        )}
                        {result.ocr?.ocr_text && (
                          <div className="ekyc-line" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "10px 12px" }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#7A93B4", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>ID Card OCR</div>
                            <div style={{ fontSize: 12, color: "#C8D6E8", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{result.ocr.ocr_text}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div style={{ width: 56, background: "rgba(10,20,35,0.96)", borderLeft: "1px solid rgba(255,255,255,0.08)",
            display: "flex", flexDirection: "column", alignItems: "center", padding: "14px 0", gap: 10 }}>
            {TOOLS.map(({ key, icon: Icon, label }) => {
              const isActive = activeTool === key;
              return (
                <button key={key} title={label} onClick={() => setActiveTool((prev) => (prev === key ? null : key))} className="ekyc-dock-btn"
                  style={{ position: "relative", width: 40, height: 40, borderRadius: 11, border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: isActive ? "linear-gradient(135deg,#00C8FF,#7C5CFF)" : "rgba(255,255,255,0.05)",
                    transition: "background 0.2s" }}>
                  <Icon size={17} color={isActive ? "#fff" : "#7A93B4"} />
                </button>
              );
            })}
            <div style={{ flex: 1 }} />
            <button onClick={() => setOpen(false)} title="Hide" style={{ background: "none", border: "none", cursor: "pointer", color: "#7A93B4" }}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
